<template>
    <section class="element-item prose element-membership-panel content" :class="[currentColorScheme, panelSpacing]" :aria-label="panelTitle">
        <div class="container">
            <!-- Title -->
            <div v-if="showTitle && panelTitle" class="pb-p20">
                <h2 class="h1 mt-0 text-center mb-0" :id="titleAnchor">{{ panelTitle }}</h2>
            </div>

            <!-- Introduction -->
            <div v-if="introduction" class="text-center mb-p40" v-html="introduction"></div>

            <!-- Category Toggle -->
            <div v-if="hasMultipleCategories" class="flex justify-center mb-p40 md:mb-p60 ">
                <div class="inline-flex gap-[10px] rounded-[30px] border border-color-decoration p-[5px]">
                    <button
                        v-for="category in allCategories"
                        :key="category.id"
                        @click="selectCategory(category.id)"
                        :class="[
                            'min-h-[34px] px-[10px] py-[4px] text-[18px] font-semibold rounded-[17px] transition-colors',
                            selectedCategoryId !== category.id
                                ? 'bg-transparent font-normal hover:bg-scheme hover:text-contrast'
                                : 'btn-main bg-scheme text-contrast'
                        ]"
                    >
                        {{ category.title }}
                    </button>
                </div>
            </div>

            <!-- Membership Cards Grid -->
            <div class="grid center-links-three-cols">
                <div
                    v-for="membership in filteredMemberships"
                    :key="membership.id"
                    :class="[
                        'grid-item membership-card inner-box rounded p-8 flex flex-col justify-between',
                        membership.highlight ? 'border-2 border-solid border-color-decoration' : ''
                    ]"
                >
                    <!-- Card Title -->
                    <h3 class="mt-0 mb-4 color-base-text">
                        {{ membership.title }}
                    </h3>

                    <!-- Pricing Section -->
                    <div class="mb-4">
                        <p class="text-sm opacity-50 mb-1 color-base-text">From</p>
                        <div class="flex items-baseline gap-2">
                            <span class="h2 mt-0 mb-0 color-base-text">{{ membership.price }}</span>
                            <span class="text-lg color-base-text">/week</span>
                        </div>
                    </div>

                    <!-- Payment Terms -->
                    <p class="text-sm opacity-50 mb-6 color-base-text">
                        {{ membership.paymentTerms }}<span v-if="membership.paymentTerms && membership.paymentType"> • </span>{{ membership.paymentType }}
                    </p>

                    <!-- Description -->
                    <p class="mb-6 color-base-text">
                        {{ membership.description }}
                    </p>

                    <!-- Divider -->
                    <div class="w-full h-px border-b divider opacity-50 mb-6"></div>

                    <!-- Best For Section -->
                    <div v-if="membership.bestFor && membership.bestFor.length > 0" class="mb-8">
                        <h4 class="mb-4 mt-0 color-base-text">Best for</h4>
                        <ul class="no-list-styling list-none p-0 m-0 space-y-3">
                            <li
                                v-for="(item, index) in membership.bestFor"
                                :key="index"
                                class="flex items-start gap-3 color-base-text pl-0"
                            >
                                <!-- Checkmark Icon -->
                                <svg
                                    class="w-4 h-3 mt-2 flex-shrink-0 color-decoration"
                                    viewBox="0 0 15 11"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                >
                                    <path d="M1 5.5L5.5 10L14 1" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                                <span>{{ item }}</span>
                            </li>
                        </ul>
                    </div>


                    <!-- Join Now Button -->
                    <div class="flex justify-center mt-auto">
                        <a
                            v-if="membership.linkUrl"
                            :href="membership.linkUrl"
                            :target="membership.linkNewWindow ? '_blank' : '_self'"
                            class="btn-main"
                        >
                            {{ membership.linkTitle || 'Join now' }}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
export default {
    name: 'MembershipPanel',

    props: {
        categories: {
            type: String,
            default: '[]'
        },
        panelTitle: {
            type: String,
            default: ''
        },
        showTitle: {
            type: Boolean,
            default: false
        },
        introduction: {
            type: String,
            default: ''
        },
        titleAnchor: {
            type: String,
            default: ''
        },
        panelSpacing: {
            type: String,
            default: ''
        }
    },

    data() {
        return {
            allCategories: [],
            selectedCategoryId: null
        };
    },

    computed: {
        hasMultipleCategories() {
            return this.allCategories.length > 1;
        },

        currentColorScheme() {
            if (!this.selectedCategoryId) {
                return this.allCategories.length > 0 ? this.allCategories[0].colorScheme : 'scheme1';
            }
            const category = this.allCategories.find(cat => cat.id === this.selectedCategoryId);
            return category ? category.colorScheme : 'scheme1';
        },

        filteredMemberships() {
            if (!this.selectedCategoryId) {
                // Return all memberships if no category selected
                return this.allCategories.flatMap(cat => cat.types);
            }

            const category = this.allCategories.find(cat => cat.id === this.selectedCategoryId);
            return category ? category.types : [];
        }
    },

    mounted() {
        try {
            this.allCategories = JSON.parse(this.categories);

            // Select first category by default if multiple exist
            if (this.allCategories.length > 0) {
                this.selectedCategoryId = this.allCategories[0].id;
            }
        } catch (e) {
            console.error('Failed to parse membership categories:', e);
            this.allCategories = [];
        }
    },

    methods: {
        selectCategory(categoryId) {
            this.selectedCategoryId = categoryId;
        }
    }
};
</script>
